import { useMemo } from 'react';
import type { ProcessedRoute } from '@/routes/model';
import { fmtNumber } from '@/ui/i18n';

interface Props {
  route: ProcessedRoute;
  /** Current position (m, wrapped for loops). */
  positionM?: number;
  /** Also render the zoomed "next 2 km" strip. */
  showAhead?: boolean;
  height?: number;
  testId?: string;
}

const W = 1000;

function gradeColor(g: number): string {
  if (g >= 10) return '#9d4edd';
  if (g >= 7) return '#e63946';
  if (g >= 4) return '#f77f00';
  if (g >= 2) return '#f6c343';
  if (g > -2) return '#2ec27e';
  return '#4ea8de';
}

/** Whole-route profile with climbs highlighted + position marker, and the next 2 km zoomed (SPEC §8.4). */
export function ElevationProfile({ route, positionM, showAhead = true, height = 90, testId }: Props) {
  const pts = route.points;
  const L = route.meta.distanceM;
  const { minY, maxY, path } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of pts) {
      lo = Math.min(lo, p.y);
      hi = Math.max(hi, p.y);
    }
    const pad = Math.max(10, (hi - lo) * 0.1);
    const minY = lo - pad;
    const maxY = hi + pad;
    const H = 100;
    const step = Math.max(1, Math.floor(pts.length / 600));
    let d = `M0,${H}`;
    for (let i = 0; i < pts.length; i += step) {
      const p = pts[i]!;
      d += ` L${((p.d / L) * W).toFixed(1)},${(H - ((p.y - minY) / (maxY - minY)) * H).toFixed(1)}`;
    }
    d += ` L${W},${H} Z`;
    return { minY, maxY, path: d };
  }, [pts, L]);

  const y = (alt: number) => 100 - ((alt - minY) / (maxY - minY)) * 100;
  const ahead = useMemo(() => {
    if (!showAhead || positionM === undefined) return null;
    const segs: { x0: number; x1: number; y0: number; y1: number; g: number }[] = [];
    const sample = (d: number) => {
      let dd = d;
      if (route.meta.loop) dd = ((dd % L) + L) % L;
      else if (dd > L) return null;
      // binary search
      let lo = 0;
      let hi = pts.length - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (pts[mid]!.d <= dd) lo = mid;
        else hi = mid;
      }
      return pts[lo]!;
    };
    let aLo = Infinity;
    let aHi = -Infinity;
    const raw: { d: number; y: number; g: number }[] = [];
    for (let k = 0; k <= 2000; k += 20) {
      const p = sample(positionM + k);
      if (!p) break;
      raw.push({ d: k, y: p.y, g: p.grade });
      aLo = Math.min(aLo, p.y);
      aHi = Math.max(aHi, p.y);
    }
    const span = Math.max(20, aHi - aLo);
    for (let i = 0; i < raw.length - 1; i++) {
      const a = raw[i]!;
      const b = raw[i + 1]!;
      segs.push({
        x0: (a.d / 2000) * W,
        x1: (b.d / 2000) * W,
        y0: 100 - ((a.y - aLo) / span) * 80 - 10,
        y1: 100 - ((b.y - aLo) / span) * 80 - 10,
        g: (a.g + b.g) / 2,
      });
    }
    return { segs, gain: raw.length ? raw[raw.length - 1]!.y - raw[0]!.y : 0 };
  }, [showAhead, positionM, pts, L, route.meta.loop]);

  return (
    <div className="grid" style={{ gap: 6 }} data-testid={testId}>
      <svg
        viewBox={`0 0 ${W} 100`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
      >
        <path
          d={path}
          fill="rgba(255,255,255,0.14)"
          stroke="rgba(255,255,255,0.85)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
        {route.climbs.map((c, i) => {
          const seg = pts.filter((p) => p.d >= c.startD && p.d <= c.endD);
          if (seg.length < 2) return null;
          const d = `M${(c.startD / L) * W},100 ${seg.map((p) => `L${(p.d / L) * W},${y(p.y)}`).join(' ')} L${(c.endD / L) * W},100 Z`;
          return <path key={i} d={d} fill="rgba(226,55,42,0.6)" stroke="none" />;
        })}
        {positionM !== undefined && (
          <line
            x1={(positionM / L) * W}
            x2={(positionM / L) * W}
            y1={0}
            y2={100}
            stroke="#fff"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            data-testid="profile-marker"
          />
        )}
      </svg>
      {ahead && (
        <div>
          <div className="spread small dim">
            <span>Nächste 2 km</span>
            <span>
              {ahead.gain >= 0 ? '+' : ''}
              {fmtNumber(ahead.gain, 0)} m
            </span>
          </div>
          <svg
            viewBox={`0 0 ${W} 100`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: height * 0.7, display: 'block' }}
          >
            {ahead.segs.map((s, i) => (
              <polygon
                key={i}
                points={`${s.x0},100 ${s.x0},${s.y0} ${s.x1},${s.y1} ${s.x1},100`}
                fill={gradeColor(s.g)}
                opacity={0.85}
              />
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}
